import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { api, type TemplateSummary } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { columnAccent } from '../lib/colors.js';
import { useTeams } from '../lib/teams.js';
import { toast } from '../lib/toast.js';
import { AppShell } from '../components/AppShell.js';
import { EmptyState, Modal, PageHead, Spinner } from '../components/ui.js';

type Filter = 'all' | 'recent' | 'builtin' | 'team';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'recent', label: 'Recently used' },
  { id: 'builtin', label: 'Built-in' },
  { id: 'team', label: 'Team-created' },
];

/**
 * The team's format library — what the POC calls "Choose a format". Facilitators
 * can start a session straight from a card; contributors browse read-only.
 */
export function FormatsPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const { data: user } = useCurrentUser();
  const { teams, selectTeam, isLoading: teamsLoading } = useTeams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // The sidebar's team select and this URL can drift apart: the route wins.
  const team = teams.find((candidate) => candidate.id === teamId) ?? null;

  const templatesQuery = useQuery({
    queryKey: ['templates', teamId],
    queryFn: () => api.templates.list(teamId!),
    enabled: Boolean(teamId),
  });
  const retrosQuery = useQuery({
    queryKey: ['retros', teamId],
    queryFn: () => api.teams.retros(teamId!),
    enabled: Boolean(teamId),
  });

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [chosen, setChosen] = useState<TemplateSummary | null>(null);
  const [retroName, setRetroName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<TemplateSummary | null>(null);

  // "Recently used" is derived from the team's past retros — the server stores
  // the template name on each retro, so the filter matches on that.
  const recentlyUsed = useMemo(() => {
    const names = new Set(
      (retrosQuery.data?.retros ?? [])
        .slice()
        .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
        .map((retro) => retro.templateName),
    );
    return names;
  }, [retrosQuery.data]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (templatesQuery.data?.templates ?? []).filter((template) => {
      if (needle && !`${template.name} ${template.description}`.toLowerCase().includes(needle)) return false;
      switch (filter) {
        case 'recent':
          return recentlyUsed.has(template.name);
        case 'builtin':
          return !template.isCustom;
        case 'team':
          return template.isCustom;
        default:
          return true;
      }
    });
  }, [templatesQuery.data, query, filter, recentlyUsed]);

  const startRetro = useMutation({
    mutationFn: (input: { templateId: string; name?: string }) =>
      api.retros.create({ teamId: teamId!, ...input }),
    onSuccess: ({ retroId }) => navigate(`/retro/${retroId}`),
    onError: (error: Error) => toast.error(error.message),
  });

  const removeTemplate = useMutation({
    mutationFn: (templateIdToRemove: string) => api.templates.remove(templateIdToRemove),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates', teamId] });
      setConfirmDelete(null);
      toast.info('Format deleted.');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (!user || !teamId) return null;

  // Unknown team id — the sidebar's selection is the source of truth. The
  // redirect happens in an effect: selectTeam and navigate are state updates
  // during render otherwise, which React forbids and StrictMode double-fires.
  // One paint of nothing while the effect redirects, same as before.
  const unknownTeam = !teamsLoading && teams.length > 0 && !team;
  useEffect(() => {
    if (!unknownTeam) return;
    const fallback = teams[0];
    if (!fallback) return;
    selectTeam(fallback.id);
    navigate(`/teams/${fallback.id}/formats`, { replace: true });
  }, [unknownTeam, teams, selectTeam, navigate]);
  if (unknownTeam) return null;

  const canFacilitate = team?.role === 'FACILITATOR';

  return (
    <AppShell user={user} crumb="Formats">
      <PageHead
        eyebrow="Formats"
        title="Choose a format"
        lede={
          team
            ? `Preview the columns before you start. Team-created formats are only visible to ${team.name}.`
            : 'Preview the columns before you start.'
        }
        actions={
          canFacilitate ? (
            <Link to={`/teams/${teamId}/formats/new`} className="btn btn-primary">
              <Plus size={15} aria-hidden /> Create format
            </Link>
          ) : undefined
        }
      />

      {!canFacilitate && team ? (
        <div className="notice-neutral mb-4" role="note">
          A facilitator needs to start the retrospective for this team.
        </div>
      ) : null}

      <div className="filters">
        <label className="search">
          <Search size={16} className="i" aria-hidden />
          <span className="sr-only">Search formats</span>
          <input
            className="input"
            placeholder="Search formats"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        <div className="seg" role="group" aria-label="Filter formats">
          {FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {templatesQuery.isLoading ? (
        <Spinner label="Loading formats…" />
      ) : visible.length === 0 ? (
        <div className="card">
          <EmptyState emoji="🧩" title="No formats match">
            Try a different search or filter.
          </EmptyState>
        </div>
      ) : (
        <ul className="fgrid">
          {visible.map((template) => (
            <li key={template.id} className="fcard">
              <div className="fcard-top">
                <span className={template.isCustom ? 'badge-accent' : 'badge'}>
                  {template.isCustom ? 'Team-created' : 'Built-in'}
                </span>
                {recentlyUsed.has(template.name) ? <span className="badge-accent">Recently used</span> : null}
              </div>
              <div className="swatches" aria-hidden="true">
                {template.columns.map((_, index) => (
                  <i key={index} style={{ background: columnAccent(index) }} />
                ))}
              </div>
              <h3>{template.name}</h3>
              <p>{template.description}</p>
              <p className="meta muted">
                {template.columns.length} column{template.columns.length === 1 ? '' : 's'}
              </p>
              <div className="fcard-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={!canFacilitate}
                  title={canFacilitate ? undefined : 'Only facilitators can start a retrospective.'}
                  onClick={() => {
                    setChosen(template);
                    setRetroName('');
                  }}
                >
                  Preview &amp; start
                </button>
                {canFacilitate && template.isCustom ? (
                  <>
                    <Link
                      to={`/teams/${teamId}/formats/${template.id}`}
                      className="btn btn-sm"
                      aria-label={`Edit ${template.name}`}
                    >
                      <Pencil size={13} aria-hidden /> Edit
                    </Link>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger-text"
                      aria-label={`Delete ${template.name}`}
                      onClick={() => setConfirmDelete(template)}
                    >
                      <Trash2 size={13} aria-hidden /> Delete
                    </button>
                  </>
                ) : canFacilitate ? (
                  <Link
                    to={`/teams/${teamId}/formats/new?from=${template.id}`}
                    className="btn btn-sm"
                    aria-label={`Make ${template.name} your own format`}
                  >
                    <Copy size={13} aria-hidden /> Make your own
                  </Link>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={Boolean(chosen)}
        onClose={() => setChosen(null)}
        title={chosen ? `Start “${chosen.name}”` : ''}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setChosen(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={startRetro.isPending}
              onClick={() =>
                chosen &&
                startRetro.mutate({
                  templateId: chosen.id,
                  ...(retroName.trim() ? { name: retroName.trim() } : {}),
                })
              }
            >
              {startRetro.isPending ? 'Starting…' : 'Start retro'}
            </button>
          </>
        }
      >
        {chosen ? (
          <>
            <label className="field-l">
              Session name (optional)
              <input
                className="input"
                value={retroName}
                onChange={(event) => setRetroName(event.target.value)}
                placeholder="Sprint 42 retrospective"
                maxLength={120}
              />
            </label>
            <ul className="pv-cols">
              {chosen.columns.map((column, index) => (
                <li key={index} className="pv-col" style={{ borderLeftColor: columnAccent(index) }}>
                  <strong>
                    {column.emoji ? `${column.emoji} ` : ''}
                    {column.title}
                  </strong>
                  <span>{column.description}</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Delete this format?"
        width="sm"
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={removeTemplate.isPending}
              onClick={() => confirmDelete && removeTemplate.mutate(confirmDelete.id)}
            >
              Delete format
            </button>
          </>
        }
      >
        <p>
          The team will no longer be able to start new retrospectives with “{confirmDelete?.name}”.
          Retrospectives already run with it keep their columns and are unaffected.
        </p>
      </Modal>
    </AppShell>
  );
}
